import { createBrowserRouter, redirect } from 'react-router';
import RouteError from './components/RouteError';
import Root from './pages/Root';

// One page component per route, all lazy. `/x/:slug` is the admin portal: the Worker only
// serves that path when the slug matches the secret, so nothing here reveals it.
export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        // Pathless, so a route that fails renders its error inside the shell, not instead of it.
        errorElement: <RouteError />,
        children: [
          // Four browsing surfaces, and each is about one thing. `/` is the homepage: the places,
          // with the few advertisements leading each of them, and every card going to the place
          // rather than to the person. `/gallery` is every advertisement shuffled, a new order each
          // page load. `/browse` is the country and every way of narrowing it. `/c/:city` is one
          // place, and `:city` is the location's own id, which was seeded as its slug. `/feed` is
          // retired: All Australia is the whole country in one list, and old links land there.
          { path: '/', lazy: async () => ({ Component: (await import('./pages/Home')).default }) },
          { path: '/feed', loader: () => redirect('/browse') },
          { path: '/gallery', lazy: async () => ({ Component: (await import('./pages/Gallery')).default }) },
          { path: '/browse', lazy: async () => ({ Component: (await import('./pages/Browse')).default }) },
          // The Towel maps for the whole country, the homepage's way into the venues.
          { path: '/venues', lazy: async () => ({ Component: (await import('./pages/Venues')).default }) },
          {
            path: '/c/:city',
            lazy: async () => ({ Component: (await import('./pages/City')).default }),
          },
          {
            path: '/advertise',
            lazy: async () => ({ Component: (await import('./pages/Advertise')).default }),
          },
          { path: '/terms', lazy: async () => ({ Component: (await import('./pages/Terms')).default }) },
          {
            path: '/privacy',
            lazy: async () => ({ Component: (await import('./pages/Privacy')).default }),
          },
          {
            path: '/p/:username',
            lazy: async () => ({ Component: (await import('./pages/PublicProfile')).default }),
          },
          { path: '/login', lazy: async () => ({ Component: (await import('./pages/Login')).default }) },
          {
            path: '/set-password',
            lazy: async () => ({ Component: (await import('./pages/SetPassword')).default }),
          },
          // Punters: the public's own accounts. Registration, sign-in and reset are theirs; `/forgot`
          // and `/reset/:token` serve advertisers and agencies too, told apart by `?kind=`.
          { path: '/join', lazy: async () => ({ Component: (await import('./pages/Join')).default }) },
          { path: '/signin', lazy: async () => ({ Component: (await import('./pages/SignIn')).default }) },
          { path: '/forgot', lazy: async () => ({ Component: (await import('./pages/Forgot')).default }) },
          {
            path: '/reset/:token',
            lazy: async () => ({ Component: (await import('./pages/Reset')).default }),
          },
          { path: '/lists', lazy: async () => ({ Component: (await import('./pages/Lists')).default }) },
          {
            path: '/lists/:id',
            lazy: async () => ({ Component: (await import('./pages/ListDetail')).default }),
          },
          {
            path: '/l/:token',
            lazy: async () => ({ Component: (await import('./pages/SharedList')).default }),
          },
          { path: '/me', lazy: async () => ({ Component: (await import('./pages/AdvertiserHome')).default }) },
          {
            path: '/agency',
            lazy: async () => ({ Component: (await import('./pages/AgentProfile')).default }),
          },
          {
            path: '/x/:slug',
            lazy: async () => ({ Component: (await import('./pages/admin/Portal')).default }),
          },
          { path: '*', lazy: async () => ({ Component: (await import('./pages/NotFound')).default }) },
        ],
      },
    ],
  },
]);
